import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import StartBuildingButton from "../components/StartBuildingButton";

// Plain-language Terms of Service. Covers acceptable use, IP ownership of
// generated content (teacher owns it), no-PII rule, no medical/legal/
// financial advice, change-and-termination notice. Short on purpose.
export default function Terms() {
  const lastUpdated = "May 23, 2026";
  return (
    <div className="min-h-screen bg-[#f4f5f7] text-[#1c2d3d]">
      <header className="bg-[#1c2d3d] text-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-extrabold tracking-tight hover:opacity-80"
            data-testid="terms-home-link"
          >
            <img
              src="/brand/atomicedu-logo-mark.png"
              alt="Atomic Edu"
              className="h-9 w-9 object-contain"
            />
            Atomic Edu
          </Link>
          <div className="flex items-center gap-2">
            <StartBuildingButton />
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-bold text-white/85 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12" data-testid="terms-page">
        <div className="flex items-center gap-3 mb-3">
          <FileText className="h-7 w-7 text-[#4a6984]" />
          <p className="font-bold uppercase text-xs tracking-[0.22em] text-[#4a6984]/80">
            Terms of Service
          </p>
        </div>
        <h1 className="font-black text-4xl sm:text-5xl text-[#4a6984] tracking-tight">
          The deal in plain language.
        </h1>
        <p className="mt-4 text-[#4a6984]/80 font-medium">
          Last updated <strong>{lastUpdated}</strong>. By using Atomic Edu
          you agree to these terms.
        </p>

        <Section title="Who Atomic Edu is for">
          <p>
            Atomic Edu is built for teachers, instructional coaches, and
            other K–12 education professionals. You confirm you are an
            adult with the authority to plan classroom learning.
          </p>
        </Section>

        <Section title="Your account">
          <ul>
            <li>
              Keep your sign-in credentials private. You are responsible
              for activity that happens under your account.
            </li>
            <li>
              One account per teacher. Don't share a single account across
              a department or a school.
            </li>
            <li>
              We may suspend or terminate accounts used to abuse the
              service, to harass others, or to generate content that
              violates these terms.
            </li>
          </ul>
        </Section>

        <Section title="Who owns the lessons">
          <p>
            <strong>You do.</strong> The lesson plans, teacher scripts,
            slide decks, and exports Atomic Edu generates for you are yours
            to use, share with your colleagues, store in your school-board
            systems, and adapt freely for your classroom. Atomic Edu
            retains no copyright in your generated lessons.
          </p>
          <p>
            Atomic Edu's source code, brand, prompts, and underlying
            pedagogical playbooks remain the property of Atomic Edu.
          </p>
        </Section>

        <Section title="What you cannot do">
          <ul>
            <li>
              <strong>Do not paste student personal information into the
              prompt.</strong> No names, photos, IEPs, medical notes, or
              other identifying details. The generator does not need them.
            </li>
            <li>
              Do not use Atomic Edu to generate content that harasses,
              discriminates, defames, or harms others.
            </li>
            <li>
              Do not attempt to reverse-engineer, scrape, or overload the
              service.
            </li>
            <li>
              Do not resell Atomic Edu output verbatim as if it were your
              own commercial product (small portions in your own
              instructional materials are fine — that's the point).
            </li>
          </ul>
        </Section>

        <Section title="The lessons are a starting point, not gospel">
          <p>
            Atomic Edu uses Claude (Anthropic) to draft lessons aligned with
            the Ontario curriculum. Like any AI, it occasionally
            misremembers a date, miscites an expectation code, or includes
            a small factual error. You are the teacher in the room.
            <strong> Always review and adapt</strong> before teaching.
          </p>
          <p>
            Atomic Edu does not provide legal, medical, financial, or
            mental-health advice. Lessons that touch those topics are
            classroom-level only.
          </p>
          <p>
            The Math Engine is currently calibrated to SCDSB scope and
            sequence. Teachers outside Simcoe County should review math
            unit sequencing against their board's expectations before
            teaching.
          </p>
        </Section>

        <Section title="Changes to the service">
          <p>
            We will keep adding features and refining the prompt layers.
            We may also change pricing, retire features that nobody uses,
            or pause the service for maintenance. Material changes will be
            announced in-app.
          </p>
        </Section>

        <Section title="Ending the relationship">
          <p>
            You can delete your account at any time from your profile
            menu. That permanently removes your activities, sessions, and
            profile data — see the <Link className="ae-link" to="/privacy">Privacy Policy</Link>.
            We can also terminate accounts that violate these terms.
          </p>
        </Section>

        <Section title="Liability">
          <p>
            Atomic Edu is provided "as is". To the extent permitted by law,
            Atomic Edu is not liable for indirect, incidental, or
            consequential losses arising from using or being unable to use
            the service.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These terms are governed by the laws of the Province of
            Ontario, Canada. Disputes go through the courts of Ontario.
          </p>
        </Section>

        <div className="mt-12 pt-8 border-t border-[#4a6984]/15 text-sm text-[#4a6984]/70">
          See also: <Link className="ae-link" to="/privacy">Privacy Policy</Link>.
        </div>
      </main>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-extrabold text-2xl text-[#4a6984] tracking-tight mb-3">
      {title}
    </h2>
    <div className="ae-prose text-[#1c2d3d]/90">{children}</div>
  </section>
);
